import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { webApi } from '@mi-front/api-clients';
import { date } from '@mi-front/core';
import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { stationsEnum } from '../helpers';
import {
  useYoutubeStatsCapture,
  YoutubeStatsRangeLabel,
  YoutubeStatsStationsScope,
} from '../useYoutubeStatsCapture';
import { YoutubeStatsViewV2 } from './YoutubeStatsViewV2';

type ChannelRow = { isSelf?: boolean; title: string };

/**
 * Builds a relative window ending at the current minute (seconds and ms zeroed)
 * so query keys stay stable within the same clock minute for React Query caching.
 */
function youtubeStatsRangeFromHoursBack(hoursBack: number) {
  const toMoment = date().startOf('minute');
  return {
    from: toMoment.clone().subtract(hoursBack, 'hours').toISOString(),
    to: toMoment.toISOString(),
  };
}

/** Drops unknown ids; if none left, prefers catalog `isSelf` rows then any id. */
function resolveSelectedStationsForCatalog(prev: string[], channels: Record<string, ChannelRow>) {
  const ids = Object.keys(channels);
  if (!ids.length) {
    return [];
  }

  const valid = new Set(ids);
  const kept = prev.filter((id) => valid.has(id));
  if (kept.length > 0) {
    return kept.length === prev.length && kept.every((id, i) => id === prev[i]) ? prev : kept;
  }

  const selfIds = ids.filter((id) => channels[id]?.isSelf);
  return selfIds.length > 0 ? selfIds : [ids[0]];
}

/**
 * BFF-backed YouTube country viewers stats: one fetch per station/window; channel scope is applied in the view.
 */
export function YoutubeStatsContainerV2() {
  const youtubeStatsCapture = useYoutubeStatsCapture();
  const [dateRange, setDateRange] = useState(() => ({
    label: '1hr',
    range: youtubeStatsRangeFromHoursBack(1),
  }));

  const dateRanges = useMemo(() => {
    const toMoment = date().startOf('minute');
    const to = toMoment.toISOString();
    const fromHoursAgo = (hours: number) => toMoment.clone().subtract(hours, 'hours').toISOString();
    return [
      { label: '1hr', range: { from: fromHoursAgo(1), to } },
      { label: '3hrs', range: { from: fromHoursAgo(3), to } },
      { label: '6hrs', range: { from: fromHoursAgo(6), to } },
      { label: '12hrs', range: { from: fromHoursAgo(12), to } },
      { label: '24hrs', range: { from: fromHoursAgo(24), to } },
    ];
  }, [dateRange.range.from, dateRange.range.to]);

  const station = useActiveStation();
  const youtubeStatsEnabled = useYoutubeStatsEnabled(station);
  const [stationsSelection, setStationsSelectionState] = useState(stationsEnum.current);

  const setStationsSelection = useCallback(
    (value: string | null) => {
      if (!value) {
        return;
      }

      youtubeStatsCapture.stationsScopeChanged({ scope: value as YoutubeStatsStationsScope });
      setStationsSelectionState(value);
    },
    [youtubeStatsCapture],
  );

  const handleDateRangeChange = useCallback(
    (selectedLabel: string) => {
      const selectedDateRange = dateRanges.find((range) => range.label === selectedLabel);
      if (!selectedDateRange) {
        return;
      }

      youtubeStatsCapture.dateRangeChanged({
        range_label: selectedLabel as YoutubeStatsRangeLabel,
      });
      setDateRange(selectedDateRange);
    },
    [dateRanges, youtubeStatsCapture],
  );

  const {
    data: countryStats,
    isLoading,
    isFetching,
  } = webApi.useGetYoutubeIntegrationStatsCountryViewers(
    station.id,
    {
      from: dateRange.range.from,
      to: dateRange.range.to,
    },
    {
      query: {
        enabled: youtubeStatsEnabled && Boolean(station.id),
        placeholderData: keepPreviousData,
        /** Keeps data fresh for 5m so focus/reconnect/mount does not refetch constantly. */
        staleTime: 5 * 60 * 1000,
      },
    },
  );

  const { stationsList, channelCatalogFingerprint } = useMemo(() => {
    const channels = countryStats?.metadata?.channels;
    if (!channels) {
      return {
        stationsList: [] as { channelId: string; channelName: string }[],
        channelCatalogFingerprint: '',
      };
    }
    return {
      stationsList: Object.entries(channels).map(([channelId, meta]) => ({
        channelId,
        channelName: meta.title,
      })),
      channelCatalogFingerprint: Object.keys(channels).sort().join('|'),
    };
  }, [countryStats]);

  const [selectedStations, setSelectedStationsState] = useState<string[]>(() =>
    station.youtubeChannelId ? [station.youtubeChannelId] : [],
  );

  const setSelectedStations = useCallback(
    (channelIds: string[]) => {
      youtubeStatsCapture.stationFilterApplied({
        selected_channel_count: channelIds.length,
      });
      setSelectedStationsState(channelIds);
    },
    [youtubeStatsCapture],
  );

  useEffect(() => {
    if (!channelCatalogFingerprint || !countryStats?.metadata?.channels) {
      return;
    }
    setSelectedStationsState((prev) =>
      resolveSelectedStationsForCatalog(prev, countryStats.metadata.channels),
    );
  }, [channelCatalogFingerprint, countryStats]);

  return (
    <YoutubeStatsViewV2
      stats={countryStats}
      isLoading={isLoading}
      isStatsFetching={isFetching}
      stationsSelection={stationsSelection}
      setStationsSelection={setStationsSelection}
      stationsList={stationsList}
      selectedStations={selectedStations}
      setSelectedStations={setSelectedStations}
      stationYoutubeChannelId={station.youtubeChannelId}
      dateRanges={dateRanges}
      handleDateRangeChange={handleDateRangeChange}
      dateRange={dateRange}
    />
  );
}
